import {
  Ellipsis,
  FolderIcon,
  TooltipTrigger,
} from "@visiblethread/react-components";
import Flex, { GAP_CLASS } from "components/Flex";
import {
  isSiteBlocked,
  SITE_ACCESS_MESSAGES,
  SITE_ACCESS_STATUS,
} from "constants/sharepointAccess";
import { ReactComponent as SiteIcon } from "icons/visi/site.svg";
import { TriangleAlert } from "lucide-react";
import { arrayOf, shape, string } from "prop-types";

import { getDocumentIcon } from "../../../../utils";
import styles from "./NameCell.module.scss";

const PendingSiteName = ({ item }) => (
  <Flex
    as="span"
    gap={GAP_CLASS.GAP_200}
    className={styles.nameCell}
    data-clarity-mask="True"
    data-testid="pending-site"
    aria-disabled="true"
  >
    <Ellipsis text={item.name} />
  </Flex>
);

PendingSiteName.propTypes = {
  item: shape({ name: string.isRequired }).isRequired,
};

const BlockedSiteName = ({ item }) => {
  const message = SITE_ACCESS_MESSAGES[item.accessStatus];
  const explanation = [message.title, message.text].filter(Boolean).join(". ");

  return (
    <TooltipTrigger
      classes={styles.nameCell__tooltip}
      content={`${item.name}. ${explanation}`}
    >
      <Flex
        as="span"
        gap={GAP_CLASS.GAP_200}
        className={styles.nameCell}
        data-clarity-mask="True"
        data-testid="blocked-site"
        aria-disabled="true"
      >
        <Flex
          as="span"
          className={styles.nameCell__indicator}
          role="img"
          aria-label={explanation}
        >
          <TriangleAlert
            size={16}
            className={styles.nameCell__icon}
            aria-hidden="true"
          />
        </Flex>
        <Ellipsis text={item.name} enableTooltip={false} />
      </Flex>
    </TooltipTrigger>
  );
};

BlockedSiteName.propTypes = {
  item: shape({
    name: string.isRequired,
    accessStatus: string.isRequired,
  }).isRequired,
};

const NameCell = ({ row, appliedFiles }) => {
  const item = row.original;
  if (item.accessStatus === SITE_ACCESS_STATUS.CHECKING) {
    return <PendingSiteName item={item} />;
  }
  if (isSiteBlocked(item)) {
    return <BlockedSiteName item={item} />;
  }
  if (
    item.type === "folder" ||
    item.type === "collection" ||
    item.type === "site"
  ) {
    return (
      <span
        className="file_explorer__list__item__name_cell"
        style={{ cursor: "pointer" }}
        role="button"
        data-clarity-mask="True"
      >
        {item.type === "site" ? <SiteIcon /> : <FolderIcon />}
        <Ellipsis text={item.name} style={{ flex: 1 }} />
      </span>
    );
  }

  const isApplied = appliedFiles.some((appliedFile) => {
    if (appliedFile.source === "Sharepoint") {
      return (
        appliedFile.content.hash === item.hash &&
        appliedFile.content.name === item.name
      );
    }
    return (
      appliedFile.hash === item.hash && appliedFile.content.name === item.name
    );
  });

  return (
    <span
      className="file_explorer__list__item__name_cell"
      style={{ cursor: !isApplied ? "pointer" : null }}
      tabIndex={0}
      role="button"
      data-clarity-mask="True"
    >
      {getDocumentIcon(item.name)}
      <Ellipsis text={item.name} style={{ flex: 1 }} />
    </span>
  );
};

NameCell.propTypes = {
  row: shape({
    original: shape({
      type: string.isRequired,
      name: string.isRequired,
      hash: string,
      accessStatus: string,
    }).isRequired,
  }).isRequired,
  appliedFiles: arrayOf(
    shape({
      content: shape({
        name: string.isRequired,
        hash: string,
      }),
      source: string,
      hash: string,
    })
  ).isRequired,
};

export default NameCell;
